/* Keeps the tier bar honest after every cart mutation. The theme dispatches
   its own cart events; we listen to all of them plus a fetch interception of
   /cart/*.js so the bar never shows a stale total. */
class AnyraGiftTiers extends HTMLElement {
  connectedCallback() {
    this.fill = this.querySelector('[data-tiers-fill]');
    this.message = this.querySelector('[data-tiers-message]');
    this.stops = Array.from(this.querySelectorAll('[data-tiers-stop]'));
    this.top = parseInt(this.dataset.top || '0', 10);
    this.format = this.dataset.currencyFormat || 'Rs {{amount}}';

    // The drawer replaces this element with fresh server HTML on every cart
    // change, and that HTML already carries the final width, so the CSS
    // transition has nothing to travel from and the bar appears to jump.
    // Start from the last known width and animate to the rendered one.
    if (this.fill) {
      const target = this.fill.style.width || '0%';
      const previous = AnyraGiftTiers.lastWidth ?? '0%';
      if (previous !== target) {
        this.fill.style.transition = 'none';
        this.fill.style.width = previous;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.fill.style.transition = '';
            this.fill.style.width = target;
          });
        });
      }
      AnyraGiftTiers.lastWidth = target;

      // Same for the stops: pop any that are newly reached in the fresh markup.
      const reachedNow = this.stops.filter((s) => s.classList.contains('is-reached')).length;
      if (AnyraGiftTiers.lastReached != null && reachedNow > AnyraGiftTiers.lastReached) {
        const stop = this.stops.filter((s) => s.classList.contains('is-reached'))[reachedNow - 1];
        if (stop) {
          stop.classList.add('is-just-reached');
          stop.addEventListener('animationend', () => stop.classList.remove('is-just-reached'), { once: true });
        }
      }
      AnyraGiftTiers.lastReached = reachedNow;
    }

    this.onCart = this.onCart.bind(this);
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build'].forEach(
      (e) => document.addEventListener(e, this.onCart)
    );
    AnyraGiftTiers.patchFetch();
  }

  disconnectedCallback() {
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build'].forEach(
      (e) => document.removeEventListener(e, this.onCart)
    );
  }

  // Themes vary in what they put on the event; fall back to asking the cart.
  async onCart(event) {
    const cart = event?.detail?.cart;
    const total = cart && typeof cart.total_price === 'number' ? AnyraGiftTiers.basis(cart) : await AnyraGiftTiers.fetchTotal();
    if (typeof total === 'number') this.render(total);
  }

  // Cart total minus what the free-gift lines cost, same basis as the Liquid.
  static basis(cart) {
    const gifts = (cart.items || []).filter((i) => i.properties && i.properties._anyra_gift);
    return cart.total_price - gifts.reduce((s, i) => s + (i.final_line_price || 0), 0);
  }

  static async fetchTotal() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return AnyraGiftTiers.basis(await res.json());
    } catch { return null; }
  }

  // One-time hook so an add-to-cart from anywhere refreshes every bar on the page.
  static patchFetch() {
    if (AnyraGiftTiers._patched) return;
    AnyraGiftTiers._patched = true;
    const original = window.fetch;
    window.fetch = async function (...args) {
      const response = await original.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        AnyraGiftTiers.fetchTotal().then((total) => {
          if (typeof total !== 'number') return;
          document.querySelectorAll('anyra-gift-tiers').forEach((el) => el.render(total));
        });
      }
      return response;
    };
  }

  money(cents) {
    const rupees = Math.ceil(cents / 100);
    return this.format.replace(/\{\{\s*amount[^}]*\}\}/, rupees.toLocaleString('en-IN'));
  }

  render(total) {
    this.dataset.cartTotal = total;

    if (this.fill && this.top > 0) {
      /* Piecewise: stops sit at even positions (data-pos), the fill is linear
         between neighbouring stops, so 499 lands exactly on the 499 stop. */
      let prevAmt = 0, prevPos = 0, pct = 0, settled = false;
      this.stops.forEach((stop) => {
        if (settled) return;
        const amt = parseInt(stop.dataset.amount, 10);
        const pos = parseFloat(stop.dataset.pos || '0');
        if (total >= amt) { pct = pos; }
        else {
          const span = amt - prevAmt, into = total - prevAmt;
          pct = span > 0 && into > 0 ? prevPos + (pos - prevPos) * (into / span) : prevPos;
          settled = true;
        }
        prevAmt = amt; prevPos = pos;
      });
      const w = `${Math.min(100, pct)}%`;
      this.fill.style.width = w;
      AnyraGiftTiers.lastWidth = w;
    }

    let next = null;
    this.stops.forEach((stop) => {
      const amount = parseInt(stop.dataset.amount, 10);
      const reached = total >= amount;
      const was = stop.classList.contains('is-reached');
      stop.classList.toggle('is-reached', reached);

      // Pop only on the transition into reached, never on every render, so the
      // animation marks the moment the reward is earned rather than decorating.
      if (reached && !was) {
        stop.classList.remove('is-just-reached');
        void stop.offsetWidth; // restart the animation
        stop.classList.add('is-just-reached');
        stop.addEventListener('animationend', () => stop.classList.remove('is-just-reached'), { once: true });
      }
      if (!reached && (next === null || amount < next.amount)) {
        // Short stop label, same as the server sentence (21 Sep), not the long reward string.
        next = { amount, label: (stop.querySelector('.anyra-tiers__what')?.textContent?.trim() || stop.dataset.reward || '') };
      }
    });

    AnyraGiftTiers.lastReached = this.stops.filter((s) => s.classList.contains('is-reached')).length;

    if (!this.message) return;
    /* Name what is already unlocked before what is next. At 699 the lip oil
       tier is reached; saying only "add 800 for the serum" reads as if it
       were not (client, 11 Sep). */
    var reached = null;
    this.stops.forEach(function (stop) {
      var amount = parseInt(stop.dataset.amount || '0', 10);
      if (total >= amount) reached = (stop.querySelector('.anyra-tiers__what')?.textContent?.trim() || stop.dataset.reward || reached);
    });
    /* "You're X away from ..." (client, 21 Sep, The Bare Bar reference). Same
       phrasing as the Liquid. */
    /* Labels keep the case typed in theme settings (client, 24 Sep 19:41:
       "Free Lip Oil or Lip Balm unlocked. You're X away from a Free Rose &
       Seed Brightening Serum"). */
    /* 2 Oct: labels can arrive entity-encoded ("Rose &amp; Seed"), which then printed literally. Decode once,
       then escape once for innerHTML. */
    var decode = function (t) { var d = document.createElement('textarea'); d.innerHTML = t; return d.value; };
    var esc = function (t) { return t.replace(/&/g, '&amp;').replace(/</g, '&lt;'); };
    var label = next ? esc(decode(decode(next.label))) : '';
    var nextPhrase = /delivery/i.test(label) ? label.replace(/, prepaid/i, ' on prepaid orders') : 'a ' + label;
    var shortReached = reached ? reached.replace(/, prepaid/i, '') : '';
    this.message.innerHTML = next
      ? (reached
          ? `You're <strong>${this.money(next.amount - total)}</strong> away from ${nextPhrase}.` /* 2 Oct: ticks show what is unlocked */
          : `You're <strong>${this.money(next.amount - total)}</strong> away from ${nextPhrase}.`)
      : this.dataset.completeMessage || 'Every reward unlocked.';
  }
}
customElements.define('anyra-gift-tiers', AnyraGiftTiers);
